import { useState, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { ROUTING_ADD_FIELD, ROUTING_ADD_CONDITION, ROUTING_SELECT_REDIRECT_TYPE, ROUTING_SELECT_FALLBACK_TYPE, ROUTING_CONDITION_SET } from 'FormsUI/constants/TrackingInteractionKeys/Editor/OptionsTab';
import { REDIRECT_TYPES, INITIAL_CONDITION_REDIRECT, SINGLE_REDIRECT, ROUTE_STATE } from 'FormsUI/constants/Routing';
import { setRoutingToggled, getBranchDefaultOperation, setFallbackType, setFallbackValue, setRoutingPolicyProperty, getReorderedRoutingPolicy, setRoutingListKey, setRoutingListValue, setRoutingFilterKey, addNewRoutingPolicy, removeRoutingPolicy, removeRoutingPolicies, removeRoutingList, appendRoutingList, getDefaultMeetingIdFromRouting, validateFormRoutingFields, validateFormRoutingRoutes, validateFormRoutingConditions, getNumberOfPoliciesFromRouting, getNumberOfListsFromRouting, getIsRoutingEnabled, validateFormRoutingDefaultRoutes } from 'FormsUI/utils/Routing';

// @ts-expect-error untyped dependency
import { getFormGuid } from 'FormsUI/selectors/FormDefinition';
import useFetchFormRouting from 'FormsUI/api/dataFetchingClient/hooks/useFetchFormRouting';
import useRoutingValidation from 'FormsUI/components/hooks/routing/useRoutingValidation';
import { getIsNotFoundError } from 'FormsUI/api/dataFetchingClient/hooks/useFetchFormRouting';
const useFormRouting = () => {
  const formId = useSelector(getFormGuid);
  const {
    formRouting: formRoutingFromApi,
    isErrored: formRoutingErrored,
    formRoutingLoading: formRoutingLoadingFromApi,
    hasAccessToRouting,
    showFetchRoutingError,
    refetch
  } = useFetchFormRouting();
  const {
    addSimpleRouteValidation,
    addConditionalRouteValidation,
    removeAllValidation,
    addFallbackRouteValidationError,
    addConditionalRouteValueValidation,
    removeConditionalRouteValueValidation,
    removeFallbackRouteValidationError,
    removeFieldValidationError,
    removeConditionValidationError,
    addFieldValidationError,
    addConditionValidationError
  } = useRoutingValidation();
  const [formRouting, setFormRouting] = useState(null);
  const hasLoadedForTheFirstTime = useRef(false);
  const formRoutingLoading = formRoutingLoadingFromApi || !hasLoadedForTheFirstTime.current && hasAccessToRouting;
  useEffect(() => {
    if (formRoutingFromApi && !formRoutingLoadingFromApi && !hasLoadedForTheFirstTime.current) {
      setFormRouting(formRoutingFromApi);
      hasLoadedForTheFirstTime.current = true;
    }
  }, [formRoutingFromApi, formRoutingLoadingFromApi]);
  useEffect(() => {
    if (!formId && formRouting) {
      setFormRouting(null);
      hasLoadedForTheFirstTime.current = false;
    }
  }, [formId, formRouting]);
  const redirectType = useMemo(() => {
    if (formRouting) {
      return formRouting.routeState === ROUTE_STATE.CONDITIONAL_ROUTE ? REDIRECT_TYPES.CONDITIONAL : REDIRECT_TYPES.SINGLE;
    }
    return REDIRECT_TYPES.SINGLE;
  }, [formRouting]);
  const isConditionalRedirect = redirectType === REDIRECT_TYPES.CONDITIONAL;
  const formRoutingExists = Boolean(formRouting && getIsRoutingEnabled(formRouting) && !formRoutingErrored && !formRoutingLoading);
  const meetingIdForPreview = useMemo(() => formRouting ? getDefaultMeetingIdFromRouting(formRouting) : undefined, [formRouting]);
  const formRoutingEnabled = useMemo(() => Boolean(formRoutingExists && meetingIdForPreview), [formRoutingExists, meetingIdForPreview]);
  const refetchRouting = () => {
    if (hasAccessToRouting) {
      return refetch().then(({
        data
      }) => {
        const routingFromResponse = data === null || data === void 0 ? void 0 : data.result;
        if (routingFromResponse) {
          setFormRouting(routingFromResponse);
        } else if (formRoutingErrored) {
          showFetchRoutingError();
        }
      }).catch(err => {
        if (!getIsNotFoundError(err)) showFetchRoutingError();
      });
    }
    return Promise.resolve();
  };
  const setRoutingEnabled = toggled => {
    setFormRouting(prevRouting => {
      if (prevRouting && formRoutingFromApi) {
        if (toggled) {
          const newRouting = setRoutingToggled(prevRouting, toggled);
          if (!validateFormRoutingRoutes(newRouting)) {
            addConditionalRouteValueValidation();
          }
          if (!validateFormRoutingFields(newRouting)) {
            addFieldValidationError();
          }
          if (!validateFormRoutingConditions(newRouting)) {
            addConditionValidationError();
          }
          if (!validateFormRoutingDefaultRoutes(newRouting)) {
            addFallbackRouteValidationError();
          }
          return newRouting;
        } else {
          removeAllValidation();
          return setRoutingToggled(formRoutingFromApi, toggled);
        }
      }
      return prevRouting;
    });
  };
  const createConditionalRedirect = () => {
    removeFallbackRouteValidationError();
    const routingPolicyExistsOnApi = formRoutingFromApi && formRoutingFromApi.routingPolicyGroup.length;
    const routingPolicyGroup = routingPolicyExistsOnApi ? formRoutingFromApi.routingPolicyGroup || [] : INITIAL_CONDITION_REDIRECT.routingPolicyGroup;
    if (!routingPolicyExistsOnApi) {
      addConditionalRouteValidation();
    }
    setFormRouting({
      formId,
      routingPolicyGroup,
      defaultRouteType: INITIAL_CONDITION_REDIRECT.defaultRouteType,
      defaultRouteValue: I18n.text('defaultForm.thankYouMessage'),
      routeState: ROUTE_STATE.CONDITIONAL_ROUTE
    });
  };
  const createSingleRedirect = () => {
    addSimpleRouteValidation();
    const routingPolicyGroup = formRoutingFromApi ? formRoutingFromApi.routingPolicyGroup || [] : SINGLE_REDIRECT.routingPolicyGroup;
    setFormRouting({
      formId,
      routingPolicyGroup,
      defaultRouteType: SINGLE_REDIRECT.defaultRouteType,
      defaultRouteValue: SINGLE_REDIRECT.defaultRouteValue,
      routeState: ROUTE_STATE.SIMPLE_ROUTE
    });
  };
  const updateRedirectType = type => {
    trackEditorInteraction(ROUTING_SELECT_REDIRECT_TYPE, {
      value: type
    });
    if (type === REDIRECT_TYPES.CONDITIONAL) {
      createConditionalRedirect();
    } else createSingleRedirect();
  };
  const updateFallbackType = type => {
    if (formRouting) {
      trackEditorInteraction(ROUTING_SELECT_FALLBACK_TYPE, {
        value: type
      });
      const newRouting = setFallbackType(formRouting, type);
      if (!validateFormRoutingDefaultRoutes(newRouting)) {
        addFallbackRouteValidationError();
      } else {
        removeFallbackRouteValidationError();
      }
      setFormRouting(newRouting);
    }
  };
  const updateFallbackValue = value => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = setFallbackValue(prevRouting, value);
        if (validateFormRoutingDefaultRoutes(newRouting)) {
          removeFallbackRouteValidationError();
        } else {
          addFallbackRouteValidationError();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const updateFieldIdentifier = index => identifier => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = setRoutingPolicyProperty(prevRouting, index, identifier);
        if (validateFormRoutingFields(newRouting)) {
          removeFieldValidationError();
        }
        if (!validateFormRoutingConditions(newRouting)) {
          addConditionValidationError();
        }
        if (!validateFormRoutingRoutes(newRouting)) {
          addConditionalRouteValueValidation();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const updateRoutingListKey = routingPolicyIndex => routingListIndex => (key, value) => {
    setFormRouting(prevRouting => {
      if (formRouting) {
        const newRouting = setRoutingListKey(formRouting, routingPolicyIndex, routingListIndex, key, value);
        if (validateFormRoutingRoutes(newRouting)) {
          removeConditionalRouteValueValidation();
        } else {
          addConditionalRouteValueValidation();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const updateRoutingFilterKey = routingPolicyIndex => routingListIndex => (key, value) => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = setRoutingFilterKey(prevRouting, routingPolicyIndex, routingListIndex, key, value);
        if (key === 'propertyFilterOperation' && validateFormRoutingConditions(newRouting)) {
          removeConditionValidationError();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const updateFilterBranchSchedulingPageId = routingPolicyIndex => routingListIndex => meetingId => updateRoutingListKey(routingPolicyIndex)(routingListIndex)('routeValue', meetingId);
  const updateFilterBranchRouteValue = routingPolicyIndex => routingListIndex => routeValue => updateRoutingListKey(routingPolicyIndex)(routingListIndex)('routeValue', routeValue);
  const updateRouteType = routingPolicyIndex => routingListIndex => routeType => {
    setFormRouting(prevRouting => {
      if (formRouting) {
        const newRouting = setRoutingListKey(formRouting, routingPolicyIndex, routingListIndex, 'routeType', routeType);
        const isFormRoutingValid = validateFormRoutingRoutes(newRouting);
        if (isFormRoutingValid) {
          removeConditionalRouteValueValidation();
        } else {
          addConditionalRouteValueValidation();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const updateFilterBranchOperator = routingPolicyIndex => routingListIndex => (operator, type, fieldType, currentPropertyFilterOperator) => {
    const operation = getBranchDefaultOperation(operator, type, fieldType, currentPropertyFilterOperator);
    trackEditorInteraction(ROUTING_CONDITION_SET, {
      value: operation.operator
    });
    updateRoutingFilterKey(routingPolicyIndex)(routingListIndex)('propertyFilterOperation', operation);
  };
  const updateFilterBranchValue = routingPolicyIndex => routingListIndex => value => {
    if (formRouting) {
      const operation = setRoutingListValue(formRouting, routingPolicyIndex, routingListIndex, value);
      if (operation && operation.operator && operation.__typename) {
        updateRoutingFilterKey(routingPolicyIndex)(routingListIndex)('propertyFilterOperation', operation);
      }
    }
  };
  const addNewField = () => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = addNewRoutingPolicy(prevRouting);
        addFieldValidationError();
        addConditionalRouteValueValidation();
        addConditionValidationError();
        trackEditorInteraction(ROUTING_ADD_FIELD, {
          value: getNumberOfPoliciesFromRouting(newRouting)
        });
        return newRouting;
      }
      return prevRouting;
    });
  };
  const deleteField = policyIndex => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = removeRoutingPolicy(prevRouting, policyIndex);
        if (validateFormRoutingFields(newRouting)) {
          removeFieldValidationError();
        }
        if (validateFormRoutingRoutes(newRouting)) {
          removeConditionalRouteValueValidation();
        }
        if (validateFormRoutingConditions(newRouting)) {
          removeConditionValidationError();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const deleteFields = policyIndices => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = removeRoutingPolicies(prevRouting, policyIndices);
        if (validateFormRoutingFields(newRouting)) {
          removeFieldValidationError();
        }
        if (validateFormRoutingRoutes(newRouting)) {
          removeConditionalRouteValueValidation();
        }
        if (validateFormRoutingConditions(newRouting)) {
          removeConditionValidationError();
        }
        return newRouting;
      }
      return prevRouting;
    });
  };
  const addNewCondition = policyIndex => () => {
    setFormRouting(prevRouting => {
      if (prevRouting) {
        const newRouting = appendRoutingList(prevRouting, policyIndex);
        addConditionalRouteValueValidation();
        addConditionValidationError();
        const numberOfConditions = getNumberOfListsFromRouting(newRouting);
        trackEditorInteraction(ROUTING_ADD_CONDITION, {
          value: numberOfConditions
        });
        return newRouting;
      }
      return prevRouting;
    });
  };
  const deleteCondition = policyIndex => filterroutingListIndex => () => {
    if (formRouting) {
      setFormRouting(prevRouting => {
        if (prevRouting) {
          const newRouting = removeRoutingList(formRouting, policyIndex, filterroutingListIndex);
          if (validateFormRoutingRoutes(newRouting)) {
            removeConditionalRouteValueValidation();
          }
          if (validateFormRoutingConditions(newRouting)) {
            removeConditionValidationError();
          }
          return newRouting;
        }
        return prevRouting;
      });
    }
  };
  const reorderFields = (sourceIndex, targetIndex) => {
    if (formRouting) {
      setFormRouting(getReorderedRoutingPolicy(formRouting, sourceIndex, targetIndex));
    }
  };
  return {
    routing: {
      formRouting,
      formRoutingLoading,
      formRoutingErrored,
      isConditionalRedirect,
      redirectType,
      formRoutingExists,
      formRoutingEnabled,
      meetingIdForPreview
    },
    actions: {
      refetchRouting,
      setRoutingEnabled,
      createConditionalRedirect,
      createSingleRedirect,
      updateRedirectType,
      updateFallbackType,
      updateFallbackValue,
      updateFieldIdentifier,
      addNewField,
      deleteField,
      deleteFields,
      addNewCondition,
      deleteCondition,
      updateFilterBranchSchedulingPageId,
      updateFilterBranchRouteValue,
      updateRouteType,
      updateFilterBranchOperator,
      updateFilterBranchValue,
      reorderFields,
      setFormRouting
    }
  };
};
export default useFormRouting;